import React, { useState } from 'react';
import Modal from 'react-modal';
import { useIdleTimer } from 'react-idle-timer';
import './AutoLogout.css';
import inactiveIcon from '../../images/inactive-icon.svg';
import timeoutIcon from '../../images/timeout-icon.svg';
import { ping, logout, logoutRedirect } from '../../service/AuthService';

// Total time of TRIGGER_LOGOUT_WARNING_MIN and LOGOUT_WARNING_MIN should ALWAYS match MAX INACTIVE INTERVAL in App layer
const TRIGGER_LOGOUT_WARNING_SEC = 600;  // 10 minutes until warning prompt is triggered
const LOGOUT_WARNING_SEC = 300;          // warning prompt lasts 5 minutes until user refreshes or logs out

const EVENTS_THROTTLE_SEC = 20; // only register events within 20 sec windows to prevent spamming ping to backend
const SESSION_EVENTS = [ // events that denote user activity
    "load",         // page load
    "click",        // mouse click
    "keydown",      // key press
    "touchstart",   // mobile touch
];

export default function AutoLogout(props) {
    const [isAutoLogoutModalOpen, setIsAutoLogoutModalOpen] = useState(false);
    const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);
    const [countdownText, setCountdownText] = useState((LOGOUT_WARNING_SEC / 60) + " minutes");

    // gets called when a SESSION_EVENT is triggered by user
    const onAction = (e) => {
        if (!isAutoLogoutModalOpen && !isSessionExpiredModalOpen) {
            ping();
        }
    }

    // trigger logout warning modal
    const onPrompt = () => {
        setIsAutoLogoutModalOpen(true);

        // update countdown text every minute
        setInterval(() => {
            let countdownMinute = Math.ceil(getRemainingTime() / 1000 / 60);
            let minuteText = countdownMinute > 1 ? " minutes" : " minute";
            setCountdownText(countdownMinute + minuteText);
        }, 1000 * 60);
    }

    // trigger session expired modal
    const onIdle = () => {
        setIsAutoLogoutModalOpen(false);
        setIsSessionExpiredModalOpen(true);
        logout();
    }

    // called when the auto logout modal is closed via user click of 'Stay Signed In' button
    const staySignedIn = () => {
        setIsAutoLogoutModalOpen(false);
        ping();
        reset();
    }

    const modalLogout = () => {
        logoutRedirect();
    }

    const { reset, getRemainingTime } = useIdleTimer({
        onAction,
        onPrompt,
        onIdle,
        timeout: 1000 * TRIGGER_LOGOUT_WARNING_SEC,
        promptTimeout: 1000 * LOGOUT_WARNING_SEC,
        events: SESSION_EVENTS,
        eventsThrottle: 1000 * EVENTS_THROTTLE_SEC,
        stopOnIdle: true
    })

    return (
        <>
            {/* logout warning modal */}
            <Modal
                isOpen={isAutoLogoutModalOpen}
                overlayClassName="autologout-overlay"
                className="autologout-modal"
                ariaHideApp={false}
            >
                <div className="autologout-overlay" id="AutoLogoutModal">
                    <div className="cb-modal-dialog" role="document">
                        <div className="cb-modal-content">
                            <div className="cb-modal-header">
                                <img src={inactiveIcon} alt="inactive" />
                                <h4 className="cb-modal-title" data-testid="autologout-title">Are you still there?</h4>
                            </div>
                            <div className="cb-modal-body">
                                <p>It looks like you've been inactive for a while. For security reasons, you will be signed out in <strong id="AutoLogoutCountdown">{countdownText}</strong>.</p>
                            </div>
                            <div className="cb-modal-footer">
                                <button type="button" className="cb-btn stay-in" data-dismiss="cb-modal" onClick={staySignedIn}>Stay signed in</button>
                                <button type="button" className="cb-btn sign-out" data-dismiss="cb-modal" onClick={modalLogout}>Sign out</button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            {/* session expired modal */}
            <Modal
                isOpen={isSessionExpiredModalOpen}
                overlayClassName="autologout-overlay"
                className="autologout-modal"
                ariaHideApp={false}
            >
                <div className="autologout-overlay" id="SessionExpiredModal">
                    <div className="cb-modal-dialog" role="document">
                        <div className="cb-modal-content">
                            <div className="cb-modal-header">
                                <img src={timeoutIcon} alt="timeout" />
                                <h4 className="cb-modal-title" data-testid="session-expired-title">Session has expired!</h4>
                            </div>
                            <div className="cb-modal-body">
                                <p>Your session has ended due to inactivity. If you wish to continue, please log back in.</p>
                            </div>
                            <div className="cb-modal-footer">
                                <button type="button" className="cb-btn stay-in" data-dismiss="cb-modal" onClick={modalLogout}>Sign back in</button>
                                <button type="button" className="cb-btn sign-out" data-dismiss="cb-modal" onClick={modalLogout}>Leave Portal</button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}